import React, { useEffect, useState, useRef } from 'react';
import './home.css';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BsArrowRight } from 'react-icons/bs';
import {config} from "../../config";
import { useLocalized } from '../../i18n/useLocalized';

const Home = () => {
    const { t } = useTranslation();
    const loc = useLocalized();
    const navigate = useNavigate();
    const [banners, setBanners] = useState(
        JSON.parse(localStorage.getItem('bannerData')) || []
    );
    const [newsItems, setNewsItems] = useState(
        JSON.parse(localStorage.getItem('activityData')) || []
    );
    const [galleryItems, setGalleryItems] = useState(
        JSON.parse(localStorage.getItem('galleryData')) || []
    );
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const carouselInterval = useRef(null);
    const touchStartX = useRef(null);

    useEffect(() => {
        // Fetch banners from the API
        const fetchBanners = async () => {
            try {
                const response = await fetch(`${config.baseUrl}/banners`);
                const data = await response.json();

                // Always sync state; only rewrite localStorage when data changed
                setBanners(data);
                const storedData = JSON.parse(localStorage.getItem('bannerData')) || [];
                if (JSON.stringify(data) !== JSON.stringify(storedData)) {
                    localStorage.setItem('bannerData', JSON.stringify(data));
                }
            } catch (error) {
                console.error('Error fetching banners:', error);
            }
        };

        // Latest news + a gallery shot feed the two bottom bento cells
        const fetchNews = async () => {
            try {
                const response = await fetch(`${config.baseUrl}/activities`);
                const data = await response.json();
                const updatedData = data.map((item) => ({
                    ...item,
                    mediaType: item.image.endsWith('.mp4') ? 'video' : 'image',
                }));

                setNewsItems(updatedData);
                const storedData = JSON.parse(localStorage.getItem('activityData')) || [];
                if (JSON.stringify(updatedData) !== JSON.stringify(storedData)) {
                    localStorage.setItem('activityData', JSON.stringify(updatedData));
                }
            } catch (error) {
                console.error('Error fetching activities:', error);
            }
        };

        const fetchGallery = async () => {
            try {
                const response = await fetch(`${config.baseUrl}/galeries`);
                const data = await response.json();

                setGalleryItems(data);
                const storedData = JSON.parse(localStorage.getItem('galleryData')) || [];
                if (JSON.stringify(data) !== JSON.stringify(storedData)) {
                    localStorage.setItem('galleryData', JSON.stringify(data));
                }
            } catch (error) {
                console.error('Error fetching gallery:', error);
            }
        };

        fetchBanners();
        fetchNews();
        fetchGallery();
    }, []);

    useEffect(() => {
        // Setup carousel for images
        const images = banners.filter(banner => !banner.banner.endsWith('.mp4') && banner.isActive);

        if (images.length > 1) {
            carouselInterval.current = setInterval(() => {
                setCurrentImageIndex(prevIndex => (prevIndex + 1) % images.length);
            }, 7000);

            return () => clearInterval(carouselInterval.current);
        }
    }, [banners]);

    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchMove = (e) => {
        if (!touchStartX.current) return;

        const touchEndX = e.touches[0].clientX;
        const diffX = touchStartX.current - touchEndX;

        const images = banners.filter(banner => !banner.banner.endsWith('.mp4') && banner.isActive);

        if (Math.abs(diffX) > 50) {
            if (diffX > 0) {
                // Swipe left, go to next image
                setCurrentImageIndex(prevIndex => (prevIndex + 1) % images.length);
            } else {
                // Swipe right, go to previous image
                setCurrentImageIndex(prevIndex =>
                    prevIndex === 0 ? images.length - 1 : prevIndex - 1
                );
            }
            touchStartX.current = null;
        }
    };

    const activeVideos = banners.filter(banner => banner.banner.endsWith('.mp4') && banner.isActive);
    const activeImages = banners.filter(banner => !banner.banner.endsWith('.mp4') && banner.isActive);

    const heroBanner = activeImages.length > 0
        ? activeImages[currentImageIndex]
        : activeVideos[0];

    const latestNews = newsItems.length > 0 ? newsItems[0] : null;
    const galleryShot = galleryItems.length > 0 ? galleryItems[0] : null;

    const handleDotClick = (index) => {
        setCurrentImageIndex(index);
    };

    const scrollToSection = (elementId) => {
        const element = document.getElementById(elementId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <section className="home">
            <div className="heroGrid container">

                {/* --- Text card --- */}
                <div className="heroCard">
                    <h1 className="homeTitle">
                        {heroBanner ? loc(heroBanner, 'title') : 'Madagasikara Ekôlôjia'}
                    </h1>
                    <p className="heroLead">
                        {heroBanner ? loc(heroBanner, 'description') : ''}
                    </p>
                    <div className="heroActions">
                        <button className="btn" onClick={() => scrollToSection('about')}>
                            {t('home.discover')}
                            <span className="arrowCircle"><BsArrowRight/></span>
                        </button>
                        <button className="btnOutline" onClick={() => scrollToSection('contact')}>
                            {t('home.contactUs')}
                        </button>
                    </div>
                </div>

                {/* --- Media card (video or image carousel) --- */}
                <div
                    className="heroMedia"
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                >
                    {activeVideos.length > 0 && activeImages.length === 0 && (
                        <video
                            src={activeVideos[0].banner}
                            muted
                            autoPlay
                            loop
                            playsInline
                            preload="auto"
                            disablePictureInPicture
                            controls={false}
                            controlsList="nodownload nofullscreen noplaybackrate"
                        ></video>
                    )}

                    {activeImages.length > 0 && (
                        <>
                            {activeImages.map((image, index) => (
                                <img
                                    key={image.banner}
                                    src={image.banner}
                                    alt={loc(image, 'title')}
                                    style={{
                                        display: index === currentImageIndex ? 'block' : 'none',
                                    }}
                                />
                            ))}
                            <span className="photoChip">
                                {loc(activeImages[currentImageIndex], 'title')}
                            </span>
                            {activeImages.length > 1 && (
                                <div className="carousel-dots">
                                    {activeImages.map((_, index) => (
                                        <span
                                            key={index}
                                            className={`dot ${index === currentImageIndex ? 'active' : ''}`}
                                            onClick={() => handleDotClick(index)}
                                        ></span>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* --- Gallery teaser card --- */}
                {galleryShot && (
                    <div className="heroWide" onClick={() => scrollToSection('gallery')}>
                        <img src={galleryShot.image} alt={loc(galleryShot, 'title')}/>
                        <span className="photoChip">{loc(galleryShot, 'title')}</span>
                    </div>
                )}

                {/* --- Latest news widget --- */}
                {latestNews && (
                    <div className="heroNews" onClick={() => navigate(`/actualites/${latestNews.id}`)}>
                        <div className="newsThumb">
                            <img src={latestNews.image} alt={loc(latestNews, 'title')}/>
                        </div>
                        <div className="newsBody">
                            <div className="newsMeta">
                                <span className="newsTag">{t('home.newsTag')}</span>
                            </div>
                            <h3>{loc(latestNews, 'title')}</h3>
                            <span className="newsLink">
                                {t('home.readArticle')} <BsArrowRight/>
                            </span>
                        </div>
                    </div>
                )}

            </div>
        </section>
    );
};

export default Home;
